import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Calendar } from 'lucide-react';

const sections = [
  { id: 'problem', label: 'Problem', number: 1 },
  { id: 'solution', label: 'Solution', number: 2 },
  { id: 'awareness', label: 'Awareness', number: 3 },
  { id: 'consideration', label: 'Capture', number: 4 },
  { id: 'automation', label: 'Automation', number: 5 },
  { id: 'conversion', label: 'Conversion', number: 6 },
  { id: 'loyalty', label: 'Loyalty', number: 7 },
  { id: 'case-study', label: 'Case Study', number: 8 },
  { id: 'why-it-works', label: 'Results', number: 9 },
  { id: 'services', label: 'Services', number: 10 },
  { id: 'contact', label: 'Contact', number: 11 },
];

export default function ProgressBar({ isUnlocked }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const sectionsLength = sections.length;

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    
    setIsVisible(scrollTop > windowHeight * 0.5);

    let activeIndex = 0;
    sections.forEach((section, index) => {
      const element = document.getElementById(section.id);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= windowHeight * 0.5) {
          activeIndex = index;
        }
      }
    });
    
    setCurrentSection(activeIndex);
    setShowCTA(activeIndex >= sectionsLength - 3);
  }, [sectionsLength]);

  useEffect(() => {
    if (!isUnlocked) return;

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUnlocked, handleScroll]);

  const progress = ((currentSection + 1) / sectionsLength) * 100;

  const scrollToSection = useCallback((id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  if (!isUnlocked) return null;

  const currentLabel = sections[currentSection]?.label || '';

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col items-center gap-2"
            data-testid="progress-bar-desktop"
          >
            <div className="relative h-64 w-1 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="absolute top-0 left-0 w-full bg-gradient-to-b from-cyan-400 to-purple-500 rounded-full"
                style={{ height: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            
            <div className="text-center mt-2">
              <span className="text-2xl font-bold text-cyan-400">{Math.round(progress)}%</span>
              <p className="text-xs text-gray-500">Complete</p>
            </div>
            
            <div className="glass rounded-lg px-3 py-2 mt-2 text-center">
              <p className="text-xs text-gray-400">Current</p>
              <p className="text-sm text-white font-medium">{currentLabel}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-0 right-0 z-40 lg:hidden px-4"
            data-testid="progress-bar-mobile"
          >
            <div className="glass-heavy rounded-full p-3 flex items-center gap-3">
              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full"
                  style={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <span className="text-sm font-bold text-cyan-400 min-w-[45px]">{Math.round(progress)}%</span>
            </div>
          </motion.div>

          <AnimatePresence>
            {showCTA && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40"
                data-testid="completion-cta"
              >
                <div className="glass-heavy rounded-full px-6 py-4 flex items-center gap-4 border border-cyan-500/30">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    <div className="hidden sm:block">
                      <p className="text-sm text-white font-medium">Almost done!</p>
                      <p className="text-xs text-gray-400">Ready to build your funnel?</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="btn-primary py-2 px-4 text-sm flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Book a Call</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}
